import React, { useEffect, useState } from "react";
import { Upload, Trash2 } from "lucide-react";

const REQUIRED_WIDTH = 450;
const REQUIRED_HEIGHT = 450;

const checkImageDimension = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      if (this.width === REQUIRED_WIDTH && this.height === REQUIRED_HEIGHT) {
        resolve(true);
      } else {
        resolve(false);
      }
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

function ServicesCategoryModal({
  modalOpen,
  toggleModal,
  category,
  handleSaveCategory,
  isSubmitting,
}) {
  const [categoryData, setCategoryData] = useState({
    name: "",
    description: "",
    image: null,
    imagePreview: null,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (category) {
      const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
      const imagePreview = category.image
        ? category.image.startsWith("http")
          ? category.image
          : `${baseUrl}${category.image.startsWith("/") ? "" : "/"}${category.image}`
        : null;
      setCategoryData({
        name: category.name || "",
        description: category.description || "",
        image: null,
        imagePreview: imagePreview,
      });
    } else {
      setCategoryData({
        name: "",
        description: "",
        image: null,
        imagePreview: null,
      });
    }
    setError("");
  }, [category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setError("");
      const isValid = await checkImageDimension(file);
      if (!isValid) {
        setError(
          `Image dimensions must be exactly ${REQUIRED_WIDTH}x${REQUIRED_HEIGHT} pixels.`,
        );
        // Reset the file input value so the same file could be picked after correction
        e.target.value = "";
        return;
      }

      setCategoryData((prev) => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  const removeImage = () => {
    if (
      categoryData.imagePreview &&
      categoryData.imagePreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(categoryData.imagePreview);
    }
    setCategoryData((prev) => ({
      ...prev,
      image: null,
      imagePreview: null,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setError("");
    if (!categoryData.name.trim()) {
      setError("Category name is required.");
      return;
    }

    if (!categoryData.description.trim()) {
      setError("Description is required.");
      return;
    }

    if (!categoryData.image && !categoryData.imagePreview) {
      setError("Image is required.");
      return;
    }

    const formData = new FormData();
    formData.append("name", categoryData.name.trim());
    if (categoryData.description) {
      formData.append("description", categoryData.description.trim());
    }
    if (categoryData.image) {
      formData.append("image", categoryData.image);
    }

    handleSaveCategory(formData, category ? category.id : null);
  };

  const handleClose = () => {
    setError("");
    toggleModal();
  };

  if (!modalOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            {category ? "Edit Service Category" : "Add Service Category"}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={categoryData.name}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md p-2"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="description"
              value={categoryData.description}
              onChange={handleInputChange}
              maxLength={100}
              placeholder="Brief description (max 100 characters)"
              className="w-full border border-gray-300 rounded-md p-2"
              required
            />
            <div className="flex justify-between mt-1">
              <p className="text-[10px] text-gray-400">
                {categoryData.description.length}/100 characters
              </p>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image <span className="text-red-500">*</span>
            </label>
            <div className="border border-dashed border-gray-300 p-4 rounded-md mb-4 text-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                id="category-image"
              />

              {!categoryData.imagePreview && (
                <label
                  htmlFor="category-image"
                  className="flex flex-col items-center justify-center cursor-pointer"
                >
                  <Upload size={24} className="text-gray-500 mb-2" />
                  <span className="text-sm text-gray-500">
                    Click to upload image
                  </span>
                  <span className="text-[10px] text-gray-400 mt-1">
                    Required size: {REQUIRED_WIDTH}x{REQUIRED_HEIGHT} pixels
                  </span>
                </label>
              )}
              {(categoryData.imagePreview || categoryData.image) && (
                <div className="mt-2 text-center">
                  <div className="relative inline-block">
                    <img
                      src={categoryData.imagePreview}
                      alt="Preview"
                      className="h-32 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 -mt-2 -mr-2"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">
                    Click "Trash" to upload a different image
                  </p>
                </div>
              )}
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>

          <div className="flex justify-end space-x-2 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-gray-200 rounded-md"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 bg-blue-500 text-white rounded-md flex items-center ${
                isSubmitting ? "opacity-75 cursor-not-allowed" : ""
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Processing...
                </>
              ) : category ? (
                "Update"
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ServicesCategoryModal;
